/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { useNavigate, useLocation } from 'react-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultFilters, getFilters } from "datastake-daf/src/helpers/Table";
import { getColumns } from './columns';
import { MAX_SELECTIONS, labels, admLevels } from './helper';
import { filterParams } from '../../../helpers/SubjectSearch';
import { defaultSelectOptions, selectMaper } from '../../../components/Forms/projects/table/config';

export const TABS = {
  ACTIVE: 'active',
  DELETED: 'deleted',
};

export const tabs = [
  { label: 'Active', key: TABS.ACTIVE },
  { label: 'Deleted', key: TABS.DELETED },
]

export const noDeleteEntities = [
  'administrativeLevel1', 'administrativeLevel2', 'administrativeLevel4', 'administrativeLevel3', 'administrativeLevel5'
];

export const useUpdate = ({
  globals,
  getGlobals,
}) => {
  const location = useLocation();
  const goTo = useNavigate();
  const [data, setData] = useState([]);
  const [entityData, setEntityData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [itemsToMerge, setItemsToMerge] = useState([]);
  const { t } = useTranslation();
  const [mergeIsVisible, setMergeIsVisible] = useState(false);
  const { entity } = useParams();
  const [mergerLoading, setMergerLoading] = useState(false);
  const [selectOptions, setSelectOptions] = useState(defaultSelectOptions[entity]);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [activeTab, setActiveTab] = useState(params.get('activeTab') || tabs[0].key);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [visibleTabs, setVisibileTabs] = useState(Object.keys(TABS).reduce((all, key) => {
    all[key] = params.get('activeTab') === key;
    return all;
  }, {}));
  const [activeFilters, setActiveFilters] = useState(
    params.has('filters') ? getFilters(params.get('filters'))
      : defaultFilters
  );

  const noDeletion = useMemo(() => noDeleteEntities.includes(entity), [entity]);

  useEffect(() => {
    if (!visibleTabs[activeTab]) {
      setVisibileTabs((prev) => ({ ...prev, [activeTab]: true }));
    }
  }, [activeTab]);

  useEffect(() => {
    if (noDeletion) {
      setActiveTab(tabs[0].key);
    }
  }, [noDeletion]);

  useEffect(() => {
    if (typeof activeFilters === 'object' && activeFilters && Object.keys(activeFilters).length) {
      const stringifiedFilters = JSON.stringify(activeFilters);
      goTo(`${location.pathname}?&filters=${stringifiedFilters}&currentKey=${entity}`);
    }
  }, [activeFilters]);

  useEffect(() => {
    if (typeof selectMaper[entity] === 'function') {
      setSelectOptions((prev) => selectMaper[entity](prev, data, 'deletions'));
    }
  }, [data.options]);

  const removeItem = useCallback((record) => {
    setItemsToMerge((prev) => [...prev.filter((p) => p.id !== record.id)]);
  }, []);

  const checkboxOnChange = useCallback((record) => {
    if (itemsToMerge.find((i) => i.id === record.id)) {
      removeItem(record);
      return;
    }
    let items = itemsToMerge;
    if (itemsToMerge.length > MAX_SELECTIONS - 1) {
      items = itemsToMerge.slice(1);
    }
    setItemsToMerge([...items, record]);
  }, [itemsToMerge]);

  const columns = useMemo(() =>
    getColumns(entity, setEdit, setEntityData, selectOptions, t), [entity, selectOptions, t]);

  const cols = useMemo(() => [
    {
      title: '',
      key: 'select',
      render: (data) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Checkbox
            checked={!!itemsToMerge.find((i) => i.id === data.id)}
            onChange={() => checkboxOnChange(data)}
          />
        </div>
      ),
      width: 50,
    },
    ...columns,
  ], [itemsToMerge, columns, checkboxOnChange]);

  const fetchData = (defaultParams) => {
    const params = defaultParams || filterParams({ activeFilters });

    getGlobals(entity, params, () => {
      setLoading(false);
      setLoaded(true);
    });
  }

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [activeFilters]);

  useEffect(() => {
    setLoaded(false);

    if (entity !== params.get('currentKey')) {
      if (_.isMatch(defaultFilters, activeFilters)) {
        const params = filterParams({ activeFilters });
        fetchData(params);
        goTo(`${location.pathname}?&filters=${JSON.stringify(defaultFilters)}&currentKey=${entity}`);
      } else {
        setActiveFilters(defaultFilters);
      }
    }
  }, [entity]);

  useEffect(() => {
    const deleteFilters = params.get('deleteFilters') || '{}';
    goTo(`${location.pathname}?filters=${JSON.stringify(activeFilters)}&currentKey=${entity}&activeTab=${activeTab}&deleteFilters=${deleteFilters}`);
  }, [activeTab]);

  const onChangeDeleteFilters = (filters = {}) => {
    try {
      const deleteFilters = JSON.stringify(filters);
      goTo(`${location.pathname}?filters=${JSON.stringify(activeFilters)}&currentKey=${entity}&activeTab=${activeTab}&deleteFilters=${deleteFilters}`);
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    setData(globals[entity] || []);
  }, [globals[entity]]);

  const getLabel = () => {
    if (labels[entity]) {
      return t(labels[entity]);
    }
    return t(`${entity.substring(0, 1).toUpperCase()}${entity.substring(1)}`);
  }

  const mergeClicked = useCallback(() => {
    setMergeIsVisible(true);
  }, []);

  const getExtra = () => {
    const valToReturn = [];

    if (admLevels[entity]) {
      valToReturn.push({
        type: "primary",
        onClick: () => {
          setEntityData({
            level: admLevels[entity]
          });
          setIsCreate(true);
          setEdit(true);
        },
        label: t("New"),
      });
    }

    valToReturn.push(
      itemsToMerge.length > 1 ?
        {
          label: t('Merge'),
          onClick: mergeClicked,
        } : {
          tooltip: t('Select two or more subjects'),
          label: t('Merge'),
          disabled: true,
        }
    );

    return valToReturn;
  }

  const mergerColumns = useMemo(() => {
    const newColumns = [...columns].sort((a, b) => a.mergePos - b.mergePos);
    return newColumns.filter((c) => !c.noMerge);
  }, [columns]);

  useEffect(() => {
    if (mergeIsVisible) {
      setMergeIsVisible(false);
    }
  }, [data]);

  useEffect(() => {
    setItemsToMerge([]);
  }, [entity]);

  const tableColumns = useMemo(() =>
    cols.filter((c) => !c.onlyMerge), [cols]);

  return {
    getLabel,
    getExtra,
    loaded,
    loading,
    data,
    activeFilters,
    entity,
    selectOptions,
    tableColumns,
    setActiveFilters,
    entityData,
    edit,
    setEdit,
    setIsCreate,
    isCreate,
    admLevels,
    itemsToMerge,
    t,
    mergeIsVisible,
    mergerColumns,
    setMergeIsVisible,
    setMergerLoading,
    setItemsToMerge,
    mergerLoading,
    fetchData,
    removeItem,
    activeTab,
    setActiveTab,
    visibleTabs,
    onChangeDeleteFilters,
    noDeletion,
  }
};
